import { useCallback, useContext, useState } from 'react'

import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import { type AttributeInput } from '@data/shopify/storefront/types'
import { AnalyticsEventName, getProductEventPayload } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { useAddItemsToCart } from '@lib/cart/hooks'
import { type CartVariantLineItem } from '@lib/cart/types'
import { ShopContext } from '@lib/shop-context'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import SimplePortableText from '@components/simple-portable-text'
import ProductAddToCart from './product-add-to-cart'
import ProductCounter from './product-counter'

interface ProductActionsProps {
  product: SanityProductFragment
  variantIds: number[]
  inStock: boolean
  cartAttributes?: AttributeInput[]
}

const ProductActions = ({
  product,
  variantIds,
  inStock,
  cartAttributes,
}: ProductActionsProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { currencyCode } = useContext(ShopContext)
  const strings = useContext(StringsContext)

  const [quantity, setQuantity] = useState(1)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const addItemsToCart = useAddItemsToCart()

  const handleAddToCart = useCallback(async () => {
    setIsAddToCartError(false)

    const variantLineItems: CartVariantLineItem[] = variantIds.map(
      (variantId) => ({
        id: variantId,
        quantity,
        productId: product._id,
        productCategoryId: product.productCategoryId,
        collectionIds: product.collectionIds,
        attributes: cartAttributes,
      }),
    )
    const isSuccessful = await addItemsToCart(variantLineItems)

    setIsAddToCartError(!isSuccessful)

    if (isSuccessful) {
      const eventPayload = getProductEventPayload(
        product.title,
        variantIds.map((variantId) => ({
          variantId,
          // TODO: Get variant options for `item_variant`.
          options: [],
          // TODO: Get variant price for `price`.
          price: 0,
          // TODO: Get variant compare price for `discount`.
          comparePrice: 0,
          quantity,
        })),
        currencyCode,
      )
      triggerAnalyticsEvent(AnalyticsEventName.AddToCart, eventPayload)
    }
  }, [
    addItemsToCart,
    cartAttributes,
    currencyCode,
    product,
    quantity,
    triggerAnalyticsEvent,
    variantIds,
  ])

  return (
    <div className="flex flex-col gap-y-5 items-baseline w-full text-center">
      <ProductCounter
        maximumProductCount={9999}
        onUpdate={(newQuantity) => setQuantity(newQuantity)}
      />

      <ProductAddToCart
        variant="filled"
        disabled={!inStock || variantIds.length === 0}
        onClick={handleAddToCart}
        className="max-w-sm w-full"
      >
        {strings.buttonAddToCart}
      </ProductAddToCart>

      {isAddToCartError && (
        <div key="error">
          <Alert error>
            <SimplePortableText
              className="rc rc-alert rc-error"
              content={strings.cartAddToCartErrorMessage}
            />
          </Alert>
        </div>
      )}
    </div>
  )
}

export default ProductActions
